import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { api, type Bucket, type Card, type Stats } from '../lib/api';
import { useResource } from '../lib/use-resource';
import { CardList, QueueRail } from '../components/CardList';
import { CardDetail } from '../components/CardDetail';
import { Rail, BUCKETS, cardHref } from '../components/Rail';
import type { ConfirmRequest } from '../components/Confirm';
import { hasDraft, who } from '../lib/cards';
import { useRuns } from '../lib/use-runs';
import { RunsTray } from '../components/RunsTray';
import { deskLayout, readPrefs, stepTo, writePrefs, type LayoutPrefs } from '../lib/layout';

const local = () => { try { return window.localStorage; } catch { return null; } };

/**
 * The Desk: the queue on the left, the decision on the right.
 *
 * On a phone it is one of those at a time — the list, or the card — because
 * 390px cannot hold both and a squeezed two-column layout is neither.
 */
export function Desk({ bucket, cardId, stats, onConfirm, onChanged, me, operator, narrow, now }: {
  bucket: Bucket;
  cardId: number | null;
  stats: { data: Stats | null; refetch: () => Promise<void> };
  onConfirm: (r: ConfirmRequest) => void;
  onChanged: () => void;
  me: string | null;
  operator: boolean;
  narrow: boolean;
  now: number;
}) {
  const [, setLocation] = useLocation();

  const list = useResource<{ cards: Card[] }>(
    () => api.cards(bucket, bucket === 'done_today' ? 'newest' : 'oldest'),
    { pollMs: 30_000, deps: [bucket] },
  );
  const opened = useResource<{ card: Card }>(
    () => api.card(cardId as number),
    { enabled: cardId !== null, pollMs: 30_000, deps: [cardId] },
  );

  const cards = list.data?.cards ?? [];
  const title = BUCKETS.find((b) => b.key === bucket)?.label ?? 'Cards';

  // On a wide screen the first card opens itself, as the mockup shows: the
  // queue is meant to be worked, and an empty right-hand pane is a click
  // between the operator and the thing they came here for. `replace` so the
  // back button still leaves the screen rather than stepping through cards.
  useEffect(() => {
    if (narrow || cardId !== null || list.loading || cards.length === 0) return;
    setLocation(cardHref(bucket, cards[0].id), { replace: true });
  }, [narrow, cardId, list.loading, cards, setLocation, bucket]);

  const refreshAll = () => {
    list.refetch();
    opened.refetch();
    onChanged();
  };

  // fix-79: Approve answers once the card is claimed; the run is followed in the tray while he works the next card.
  const runs = useRuns(refreshAll);
  const onStarted = (c: Card) => {
    runs.start(c, `${who(c)} · card ${c.id}`);
    const ids = cards.map((x) => x.id);
    const to = stepTo(ids, c.id, 1) ?? stepTo(ids, c.id, -1);
    setLocation(to !== null ? cardHref(bucket, to) : bucket === 'needs_you' ? '/' : `/b/${bucket}`);
  };

  const showList = !narrow || cardId === null;
  const showDetail = cardId !== null && (!narrow || cardId !== null);

  // fix-74: with a card open on a desktop the nav is icons and the queue slim (Q brings it back); focus (F) hides both.
  // The choice is remembered per viewer (lib/layout.ts).
  const [prefs, setPrefs] = useState<LayoutPrefs>(() => readPrefs(local()));
  const setPref = (p: Partial<LayoutPrefs>) => setPrefs((was) => {
    const next = { ...was, ...p };
    writePrefs(local(), next);
    return next;
  });
  const card = opened.data?.card ?? null;
  const layout = deskLayout({ narrow, cardOpen: cardId !== null, hasDraft: !!card && hasDraft(card), prefs });
  const step = (dir: 1 | -1) => {
    const to = stepTo(cards.map((c) => c.id), cardId, dir);
    if (to !== null) setLocation(cardHref(bucket, to));
  };
  const onLayout = (act: 'next' | 'prev' | 'queue' | 'focus') => {
    if (act === 'next') step(1);
    if (act === 'prev') step(-1);
    if (narrow) return; // the phone has no rails to fold
    // Q with focus on leaves focus and shows the queue: the key always ends with the queue in sight.
    if (act === 'queue') setPref(layout.focus ? { focus: false, queue: 'open' } : { queue: prefs.queue === 'open' ? 'slim' : 'open' });
    if (act === 'focus') setPref({ focus: !layout.focus });
  };

  return (
    <>
      {showList && layout.nav !== 'hidden' && <Rail stats={stats.data} bucket={bucket} now={now} compact={layout.nav === 'icons'} operator={operator} />}
      <div className="desk-body">
        {showList && layout.queue === 'full' && (
          <CardList
            bucket={bucket}
            title={title}
            cards={cards}
            selectedId={cardId}
            loading={list.loading}
            error={list.error?.message ?? null}
            onSlim={!narrow && cardId !== null ? () => onLayout('queue') : undefined}
          />
        )}
        {showList && layout.queue === 'slim' && (
          <QueueRail title={title} cards={cards} selectedId={cardId} onStep={step} onShow={() => onLayout('queue')} />
        )}
        {showDetail && (
          opened.data
            ? (
              <CardDetail
                card={opened.data.card}
                onConfirm={onConfirm}
                onChanged={refreshAll}
                onStarted={onStarted}
                run={runs.runs.find((r) => r.id === opened.data?.card.id) ?? null}
                me={me}
                operator={operator}
                layout={layout}
                onLayout={onLayout}
                onBack={narrow ? () => setLocation(bucket === 'needs_you' ? '/' : `/b/${bucket}`) : undefined}
              />
            )
            : (
              <main className="detail">
                <div className="detail-scroll">
                  <p className="list-empty">
                    {opened.loading ? 'Opening the card…' : opened.error?.message ?? 'No card.'}
                  </p>
                </div>
              </main>
            )
        )}
        {!showDetail && !narrow && (
          <main className="detail">
            <div className="detail-scroll">
              <p className="list-empty">Pick a card.</p>
            </div>
          </main>
        )}
      </div>
      <RunsTray runs={runs.runs} bucket={bucket} onClose={runs.close} />
    </>
  );
}
